import {
	useState,
	useEffect,
	useRef,
} from 'react'
import {
	useSelector,
	useDispatch,
} from 'react-redux'

import {
	autocompleteStations,
	reset,
} from '../features/railStations/railStationSlice'

import {
	Box,
	Button,
	Stack,
	StackDivider,
	Input,
	InputGroup,
	LinkBox,
	LinkOverlay,
	InputRightElement,
	CloseButton,
	useOutsideClick,
} from '@chakra-ui/react'
import "/node_modules/flag-icons/css/flag-icons.min.css"

import StationDisplayElement from './ui/StationDisplayElement'

import { debounce } from '../utilities/performanceHelper'
import UserReportPopover from './UserReportPopover'
import CountryStationSelector from './station_search/CountryStationSelector'

export default function StationSearch({ setSelectedStation, prefilledText, clearOnSelect, placeholder }) {

	const { stations, isLoading, isError, message } = useSelector((state) => state.stations)
	const { user } = useSelector((state) => state.auth)
	const { journeys } = useSelector((state) => state.journeys)
	const { maps } = useSelector((state) => state.creator)

	const dispatch = useDispatch()
	const debouncedDispatch = useRef(null)

	const [queryText, setQueryText] = useState(prefilledText)
	const [showResults, setShowResults] = useState(false)
	const [showMoreResults, setShowMoreResults] = useState(false)

	const homeCountry = user ? (user.details.homeCountry ? user.details.homeCountry.code : undefined) : undefined

	function hideResults() {
		reset()
		const timer = setTimeout(() => {
			setShowResults(false)
		}, 50)
		return () => clearTimeout(timer)
	}

	useEffect(() => {
		debouncedDispatch.current = debounce((queryData) => {
			dispatch(autocompleteStations(queryData))
		}, 300)

		// Clean up the debounced function on component unmount
		return () => {
			if (debouncedDispatch.current) {
				debouncedDispatch.current.cancel()
			}
		}
	}, [])

	useEffect(() => {
		if (queryText.length >= 3) { // Min 3 chars in input query to generate results
			setShowResults(true)

			const queryData = {
				name: queryText,
				homeCountryCode: homeCountry,
			}
			if (queryText.length === 3 || queryText.length === 6 || queryText.length === 9) { // No delay on first 3 & every 3 subsequent chars for interactivity
				dispatch(autocompleteStations(queryData))
			} else {
				debouncedDispatch.current(queryData)
			}

			setShowMoreResults(false) // Hiding more results on every change in query, potentially to refine
		} else {
			setShowResults(false)

			setSelectedStation(undefined)
		}

	}, [queryText, dispatch])

	// Resetting the query text by detecting change in journeys (or maps in creator)
	useEffect(() => {
		if (!showResults || (queryText < 3))
			setQueryText('') // Need to clear it here so the name value from previous destination to origin is correctly pre-filled
		setQueryText(prefilledText)
	}, [journeys, maps]) // TODO: Fix exhaustive deps

	useEffect(() => {
		if (queryText === prefilledText)
			hideResults()
	}, [queryText])

	// Called by dropdown & advanced select, makes the callback to form & updates UI
	function setInternalSelectedStation(station) {
		if (clearOnSelect)
			setQueryText('')
		else
			setQueryText(station.name)

		setSelectedStation(station)

		hideResults()
	}

	function onDropdownStationSelect(station) {
		setInternalSelectedStation(station)
	}

	function clearButtonOnClick() {
		setQueryText('')
		setSelectedStation(undefined)
	}

	// More results
	function moreResultsOnClick() {
		dispatch(autocompleteStations({
			name: queryText,
			expandedResults: true,
			homeCountryCode: homeCountry,
		}))
		setShowMoreResults(true)
	}

	// Close results on lost focus
	const ref = useRef(null)
	useOutsideClick({
		ref: ref,
		handler: () => hideResults(),
	})

	return (
		<>
			<Box ref={ref}>
				<InputGroup>
					<Input
						value={queryText}
						onChange={(e) => setQueryText(e.target.value)}
						placeholder={placeholder}
					/>

					<InputRightElement>
						<CloseButton
							onClick={clearButtonOnClick}
						/>
					</InputRightElement>
				</InputGroup>

				{showResults && (
					<>
						<Stack
							spacing='0px'
							divider={<StackDivider borderColor='gray.200' />}
							shadow='md'
						>
							{/* Results display */}
							{stations.map((station) => (
								<>
									<LinkBox
										borderWidth='1px'
									>
										<LinkOverlay
											onClick={() => onDropdownStationSelect(station)}
										>
											<StationDisplayElement
												station={station}
											/>
										</LinkOverlay>
									</LinkBox>
								</>
							))}

							{/* More results */}
							{stations.length === 5 && !showMoreResults ? (<>
								<Button
									onClick={moreResultsOnClick}
									fontSize='sm'
									fontWeight='normal'
									maxHeight='32px'
								>
									More results
								</Button>
							</>)
								: (<></>)}

							{(showMoreResults && !isLoading) && (<>
								<UserReportPopover type='missingStation'>
									<Button
										fontSize='sm'
										fontWeight='normal'
										maxHeight='32px'
									>
										Unable to find station?
									</Button>
								</UserReportPopover>
							</>)}
						</Stack>
					</>
				)}
			</Box>

			<CountryStationSelector
				onSelectStation={setInternalSelectedStation}
			/>
		</>
	)
}